import { CredentialWithId } from "../../../../pages/credentialsConfigration/types/credentialTypes";
import { CredentialFormData } from "../hooks/useCredentialForm";

interface BuildCredentialPayloadArgs {
  formData: CredentialFormData;
  isEditMode: boolean;
  credentialData: CredentialWithId | null;
  clientId?: string;
  client_Name?: string;
  userEmail?: string | null;
  passwordToSend: string;
  secretKeyToSend: string;
}

/**
 * Pure transformer from the modal's form state to the request body
 * expected by `addEditPortalCredential`. Kept side-effect free so it's
 * trivially unit-testable.
 *
 * Behavioral notes preserved verbatim from the original inline payload:
 * - `portal_id`, `portal_url`: empty string -> `undefined` so the backend
 *   receives `null`-ish rather than empty string.
 * - `facility_name`: only sent when the selected portal is `oncoemr`.
 * - `location_id`: only sent when the selected portal is `oncoemr`.
 * - `max_concurrent`: only sent when MFA is off (the field is hidden and
 *   irrelevant for queued/MFA credentials); coerced from string to number.
 * - `is_available`: defaults to `true` on add; respects the form value on
 *   edit so existing credentials aren't silently re-enabled.
 * - `credential_id`: only populated on edit when a valid id exists, so the
 *   backend can disambiguate add vs update.
 * - `updated_by`: omitted when the user has no email.
 */
export const buildCredentialPayload = ({
  formData,
  isEditMode,
  credentialData,
  clientId,
  client_Name,
  userEmail,
  passwordToSend,
  secretKeyToSend,
}: BuildCredentialPayloadArgs) => ({
  client_name: client_Name,
  client_id: clientId,
  portal_name: formData.portal_name,
  portal_id: formData.portal_id || undefined,
  facility_name:
    formData.portal_id === "oncoemr" ? formData.facility_name : undefined,
  location_id:
    formData.portal_id === "oncoemr" ? formData.location_id : undefined,
  max_concurrent:
    formData.mfa_present === "false"
      ? Number(formData.max_concurrent)
      : undefined,
  email: formData.email,
  username: formData.username,
  password: passwordToSend,
  secret_key: secretKeyToSend,
  is_active: formData.is_active === "true",
  is_queue: formData.is_queue === "true",
  is_available: isEditMode ? formData.is_available === "true" : true,
  portal_url: formData.portal_url || undefined,
  credential_id:
    isEditMode && credentialData?.credential_id
      ? credentialData.credential_id
      : undefined,
  ...(userEmail && { updated_by: userEmail }),
  updated_at: new Date().toISOString(),
});
